import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { OrgInviteService } from '@/api/generated/services/OrgInviteService'
import { OrganizationService } from '@/api/generated/services/OrganizationService'
import type { OrgInviteResponse } from '@/api/generated/models/OrgInviteResponse'
import type { OrgJoinRequest } from '@/api/generated/models/OrgJoinRequest'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'

const JoinPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showError, showSuccess } = useAlert()

  // Get invite code from URL parameter
  const inviteCode = searchParams.get('code')

  // Form state
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState('')
  const [orgName, setOrgName] = useState('')

  // Validation errors
  const [nameError, setNameError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')

  // Loading states
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingInvite, setIsValidatingInvite] = useState(false)
  const [inviteError, setInviteError] = useState<{
    title: string
    message: string
  } | null>(null)

  // Note: localStorage clearing and logout is now handled by App.tsx for /join route
  // This ensures proper route rendering by updating isAuthenticated state

  // Redirect to login if no invite code
  useEffect(() => {
    if (!inviteCode) {
      navigate('/login')
      return
    }

    // Check if we just completed registration to skip validation
    const justRegistered = sessionStorage.getItem('just_registered')
    if (justRegistered === 'true') {
      sessionStorage.removeItem('just_registered')
      navigate('/login', { replace: true })
      return
    }

    // Validate invite code and fetch invite details
    const validateInvite = async () => {
      setIsValidatingInvite(true)
      try {
        const response: OrgInviteResponse = await OrgInviteService.appsOrganizationViewsGetOrgInvite({
          inviteId: inviteCode
        })

        // Check if invite is expired
        if (response.expired) {
          setInviteError({
            title: '유효하지 않은 초대',
            message: '초대 링크가 만료되었거나 유효하지 않습니다.'
          })
          return
        }

        setEmail(response.email)
        setOrgName(response.org_name)
      } catch (error: any) {
        console.error('Failed to validate invite:', error)
        
        setInviteError({
          title: '유효하지 않은 초대',
          message: '초대 링크가 만료되었거나 유효하지 않습니다.'
        })
      } finally {
        setIsValidatingInvite(false)
      }
    }

    validateInvite()
  }, [inviteCode, navigate, showError])

  // Clear errors when user types
  useEffect(() => {
    setNameError('')
  }, [name])

  useEffect(() => {
    setPhoneError('')
  }, [phone])

  useEffect(() => {
    setPasswordError('')
    setConfirmPasswordError('')
  }, [password, confirmPassword])

  // Form validation
  const validateForm = () => {
    let isValid = true

    // Reset errors
    setNameError('')
    setPhoneError('')
    setPasswordError('')
    setConfirmPasswordError('')

    // Name validation
    if (!name.trim()) {
      setNameError('이름을 입력해주세요.')
      isValid = false
    } else if (name.trim().length < 2) {
      setNameError('이름은 2자 이상 입력해주세요.')
      isValid = false
    }

    // Phone validation
    if (!phone.trim()) {
      setPhoneError('전화번호를 입력해주세요.')
      isValid = false
    } else if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(phone.replace(/\s/g, ''))) {
      setPhoneError('올바른 전화번호 형식을 입력해주세요. (예: 010-1234-5678)')
      isValid = false
    }

    // Password validation
    if (!password) {
      setPasswordError('비밀번호를 입력해주세요.')
      isValid = false
    } else if (password.length < 8) {
      setPasswordError('비밀번호는 8자 이상 입력해주세요.')
      isValid = false
    } else if (!/^(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
      setPasswordError('비밀번호는 영문과 숫자를 포함해야 합니다.')
      isValid = false
    }

    // Confirm password validation
    if (!confirmPassword) {
      setConfirmPasswordError('비밀번호 확인을 입력해주세요.')
      isValid = false
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('비밀번호가 일치하지 않습니다.')
      isValid = false
    }

    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (!inviteCode) {
      showError('초대 코드가 없습니다.')
      return
    }

    setIsLoading(true)

    try {
      const joinRequest: OrgJoinRequest = {
        name: name.trim(),
        phone: phone.trim(),
        password: password,
        invite_code: inviteCode
      }

      await OrganizationService.appsOrganizationViewsJoinOrganization({
        requestBody: joinRequest
      })

      showSuccess('가입이 완료되었습니다.')

      // Set flag to skip invite validation if /join is accessed again
      sessionStorage.setItem('just_registered', 'true')

      // Redirect to login after successful join
      // Use replace: true to prevent going back to join page
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 2000)
    } catch (error: any) {
      console.error('Failed to join organization:', error)
      handleApiError(error, showError, '기관 가입에 실패했습니다')
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading screen while validating
  if (isValidatingInvite) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#F4F5F6]">
        <div className="text-lg">초대 정보를 확인하는 중...</div>
      </div>
    )
  }

  // Show error screen if invite is invalid or expired
  if (inviteError) {
    return (
      <div
        className="w-full h-screen flex items-center justify-center"
        style={{ backgroundColor: designTokens.colors.gray[0] }}
      >
        <div
          className="w-[500px] bg-white rounded-lg shadow-md flex flex-col items-center"
          style={{ padding: '60px 40px' }}
        >
          {/* Error Icon */}
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: designTokens.colors.error.DEFAULT + '15'
            }}
          >
            <AlertCircle size={48} color={designTokens.colors.error.DEFAULT} />
          </div>

          {/* Title */}
          <h2
            className="mt-6 text-center"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-medium'][0],
              fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
              color: designTokens.colors.gray[6]
            }}
          >
            {inviteError.title}
          </h2>

          {/* Message */}
          <p
            className="mt-3 text-center"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
              color: designTokens.colors.gray[5]
            }}
          >
            {inviteError.message}
          </p>

          {/* Action Button */}
          <Button
            size="large"
            color="dark"
            onClick={() => navigate('/login', { replace: true })}
            className="w-full mt-8"
            style={{
              height: '48px',
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-large'][0],
              fontWeight: designTokens.typography.fontSize['body-large'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['body-large'][1].lineHeight
            }}
          >
            로그인 페이지로 이동
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-screen flex">
      {/* Left Sidebar */}
      <div className="w-[800px] bg-[#141E2F] flex-shrink-0" />

      {/* Right Content Area */}
      <div className="flex-1 bg-[#F4F5F6] flex flex-col justify-center items-center gap-10">
        {/* Join Card */}
        <div className="w-[500px] bg-white rounded-md p-[50px_40px_40px] shadow-sm">
          {/* Header */}
          <div className="flex justify-between items-center pb-5 mb-[15px]">
            <h1
              className="text-[32px] font-bold text-black"
              style={{
                fontFamily: 'Pretendard GOV',
                lineHeight: '1.193359375em'
              }}
            >
              기관 가입
            </h1>

            {/* Logo */}
            <img
              src="/images/logo-big.png"
              alt="Logo"
              className="w-[70px] h-[33px] object-contain"
            />
          </div>

          {/* Organization Info */}
          {orgName && (
            <div className="mb-6 p-4 bg-[#F8F9FA] rounded-md border">
              <div className="text-sm text-[#6C757D] mb-1">가입하려는 기관</div>
              <div
                className="text-lg font-semibold text-[#1B1B1B]"
                style={{
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
              >
                {orgName}
              </div>
            </div>
          )}

          {/* Join Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field (Read-only) */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="email"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  이메일
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="email"
                type="email"
                value={email}
                readOnly
                disabled
                size="large"
                className="w-full px-3 bg-[#F8F9FA]"
                style={{
                  height: '48px',
                  borderRadius: '4px',
                  paddingLeft: '12px',
                  paddingRight: '12px'
                }}
              />
            </div>

            {/* Name Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="name"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  이름
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="이름을 입력해주세요."
                size="large"
                state={nameError ? 'error' : 'default'}
                disabled={isLoading}
                showClearButton={false}
                className="w-full px-3"
                style={{
                  height: '48px',
                  borderRadius: '4px',
                  paddingLeft: '12px',
                  paddingRight: '12px'
                }}
              />

              {nameError && (
                <p className="text-sm text-[#E53935] mt-1">{nameError}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="phone"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  전화번호
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="전화번호를 입력해주세요. (예: 010-1234-5678)"
                size="large"
                state={phoneError ? 'error' : 'default'}
                disabled={isLoading}
                showClearButton={false}
                className="w-full px-3"
                style={{
                  height: '48px',
                  borderRadius: '4px',
                  paddingLeft: '12px',
                  paddingRight: '12px'
                }}
              />

              {phoneError && (
                <p className="text-sm text-[#E53935] mt-1">{phoneError}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="password"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  비밀번호
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력해주세요. (영문+숫자, 8자 이상)"
                size="large"
                state={passwordError ? 'error' : 'default'}
                disabled={isLoading}
                showClearButton={false}
                className="w-full px-3"
                style={{
                  height: '48px',
                  borderRadius: '4px',
                  paddingLeft: '12px',
                  paddingRight: '12px'
                }}
              />

              {passwordError && (
                <p className="text-sm text-[#E53935] mt-1">{passwordError}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="confirmPassword"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  비밀번호 확인
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="비밀번호를 다시 입력해주세요."
                size="large"
                state={confirmPasswordError ? 'error' : 'default'}
                disabled={isLoading}
                showClearButton={false}
                className="w-full px-3"
                style={{
                  height: '48px',
                  borderRadius: '4px',
                  paddingLeft: '12px',
                  paddingRight: '12px'
                }}
              />

              {confirmPasswordError && (
                <p className="text-sm text-[#E53935] mt-1">{confirmPasswordError}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="space-y-5 pt-[15px]">
              <Button
                type="submit"
                size="large"
                color="dark"
                disabled={isLoading}
                className="w-full h-[48px] text-xl"
                style={{
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
              >
                {isLoading ? '가입 중...' : '가입'}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full text-xs text-[#131416] text-center hover:underline"
                style={{
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
                disabled={isLoading}
              >
                로그인 페이지로 돌아가기
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default JoinPage